import React from "react";
import {
  Avatar, Box, Button, Chip, Dialog, DialogContent, DialogTitle, Divider,
  IconButton, Link, Stack, Typography, alpha,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";
import ClassOutlinedIcon from "@mui/icons-material/ClassOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import type { Row } from "../types";

// Shared palette — matches ClassRow + the wider report look.
const NAVY = "#1b3a6b";
const INK = "#0f172a";
const MUTED = "#64748b";
const HAIRLINE = "#e2e8f0";

const gradeLabel = (g: string | null | undefined) => {
  if (!g) return "—";
  if (/^\d+$/.test(g)) return `Grade ${g}`;
  return g;
};

const modeLabel = (m: string | null | undefined) => {
  if (!m) return "—";
  const v = m.toLowerCase();
  if (v === "online") return "Online";
  if (v === "offline" || v === "in_person") return "In Person";
  if (v === "hybrid") return "Hybrid";
  return m;
};

const to12h = (t: string | null | undefined) => {
  if (!t) return "";
  const m = /^(\d{1,2}):(\d{2})/.exec(t);
  if (!m) return t;
  let h = Number(m[1]); const mm = m[2];
  const suffix = h >= 12 ? "PM" : "AM";
  h = h % 12; if (h === 0) h = 12;
  return `${h}:${mm} ${suffix}`;
};

const formatDate = (d: string | null | undefined) => {
  if (!d) return null;
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return d;
  return dt.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
};

const statusMeta = (s: string | null | undefined): { label: string; bg: string; fg: string; border: string } => {
  const normalized = (s === "Added" ? "Active" : s) || "—";
  switch (normalized) {
    case "Active":    return { label: "Active",    bg: "#dcfce7", fg: "#166534", border: "#bbf7d0" };
    case "Future":    return { label: "Future",    bg: "#dbeafe", fg: "#1d4ed8", border: "#bfdbfe" };
    case "Ended":     return { label: "Ended",     bg: "#f1f5f9", fg: "#475569", border: "#e2e8f0" };
    case "Released":  return { label: "Released",  bg: "#fef3c7", fg: "#92400e", border: "#fde68a" };
    case "Removed":   return { label: "Removed",   bg: "#fee2e2", fg: "#991b1b", border: "#fecaca" };
    case "Cancelled": return { label: "Cancelled", bg: "#fee2e2", fg: "#991b1b", border: "#fecaca" };
    default:          return { label: normalized,  bg: "#f1f5f9", fg: "#475569", border: "#e2e8f0" };
  }
};

// A single labelled section in the modal body. Keeps the visual rhythm
// consistent without dropping into card-in-card nesting.
const Section: React.FC<{ icon: React.ReactNode; title: string; children: React.ReactNode }> = ({
  icon, title, children,
}) => (
  <Box>
    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
      <Box
        sx={{
          width: 26, height: 26, borderRadius: "8px",
          bgcolor: alpha(NAVY, 0.08), color: NAVY,
          display: "flex", alignItems: "center", justifyContent: "center",
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>
      <Typography sx={{ fontSize: 12, fontWeight: 700, color: MUTED, textTransform: "uppercase", letterSpacing: "0.05em" }}>
        {title}
      </Typography>
    </Stack>
    <Box sx={{ pl: 4 }}>{children}</Box>
  </Box>
);

// Label / value pair used inside sections.
const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <Box>
    <Typography sx={{ fontSize: 11.5, color: MUTED, fontWeight: 600 }}>{label}</Typography>
    <Box sx={{ fontSize: 13.5, color: INK, mt: 0.25 }}>{children}</Box>
  </Box>
);

type Props = {
  row: Row | null;
  open: boolean;
  onClose: () => void;
  onViewStudent?: (id: number) => void;
  onViewClass?: (id: number) => void;
  onViewStaff?: (id: number) => void;
};

export default function AssignmentDetailsModal({
  row, open, onClose, onViewStudent, onViewClass, onViewStaff,
}: Props) {
  if (!row) return null;

  const status = statusMeta(row.assignment_status);
  const initials = (row.student_name || "?")
    .split(/\s+/).filter(Boolean).map((w) => w[0]?.toUpperCase() ?? "").slice(0, 2).join("") || "?";
  const scheduleLine = row.start_time
    ? `${row.day ?? "—"} · ${to12h(row.start_time)}–${to12h(row.end_time)}`
    : (row.day || "—");
  const openFullClass = () => {
    onViewClass?.(row.class_id);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: "hidden",
        },
      }}
    >
      {/* ── Header ── */}
      <DialogTitle
        sx={{
          m: 0, p: 0,
          bgcolor: "#fff",
          borderBottom: `1px solid ${HAIRLINE}`,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ px: { xs: 2, md: 3 }, py: 2 }}>
          <Avatar sx={{ bgcolor: alpha(NAVY, 0.1), color: NAVY, width: 40, height: 40, fontSize: 14, fontWeight: 700 }}>
            {initials}
          </Avatar>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography sx={{ fontSize: 11, fontWeight: 700, color: MUTED, textTransform: "uppercase", letterSpacing: "0.05em" }}>
              Assignment Details
            </Typography>
            <Typography sx={{ fontSize: 18, fontWeight: 800, color: INK, lineHeight: 1.2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              {row.student_name || "—"}
            </Typography>
          </Box>
          <Box
            sx={{
              px: 1, py: 0.4, borderRadius: "999px",
              bgcolor: status.bg, color: status.fg,
              border: `1px solid ${status.border}`,
              fontSize: 11, fontWeight: 700,
              flexShrink: 0,
            }}
          >
            {status.label}
          </Box>
          <IconButton onClick={onClose} size="small" aria-label="Close" sx={{ ml: 0.5 }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
      </DialogTitle>

      {/* ── Body ── */}
      <DialogContent sx={{ p: { xs: 2, md: 3 }, bgcolor: "#fafbfc" }}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
            gap: { xs: 2, md: 2.5 },
          }}
        >
          {/* Section A — Student */}
          <Section icon={<SchoolOutlinedIcon fontSize="small" />} title="Student">
            <Stack spacing={1}>
              <Field label="Name">
                {onViewStudent ? (
                  <Link component="button" underline="hover"
                    onClick={() => { onViewStudent(row.student_id); onClose(); }}
                    sx={{ color: NAVY, fontWeight: 700, textAlign: "left" }}>
                    {row.student_name || "—"}
                  </Link>
                ) : (row.student_name || "—")}
              </Field>
              <Field label="Student ID">{row.reference_id || row.student_id}</Field>
              <Field label="Grade">{gradeLabel(row.school_grade)}</Field>
            </Stack>
          </Section>

          {/* Section B — Class */}
          <Section icon={<ClassOutlinedIcon fontSize="small" />} title="Class">
            <Stack spacing={1}>
              <Field label="Class title">
                {onViewClass ? (
                  <Link component="button" underline="hover"
                    onClick={openFullClass}
                    sx={{ color: NAVY, fontWeight: 700, textAlign: "left" }}>
                    {row.class_name || `Class ${row.class_id}`}
                  </Link>
                ) : (row.class_name || `Class ${row.class_id}`)}
              </Field>
              <Field label="Class ID">{row.class_code || row.class_id}</Field>
              {row.class_status && <Field label="Class status">{row.class_status}</Field>}
            </Stack>
          </Section>

          {/* Section C — Grades */}
          <Section icon={<MenuBookOutlinedIcon fontSize="small" />} title={`Grades (${row.class_grades.length})`}>
            {row.class_grades.length === 0 ? (
              <Typography sx={{ fontSize: 13, color: MUTED, fontStyle: "italic" }}>None</Typography>
            ) : (
              <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                {row.class_grades.map((g) => (
                  <Chip
                    key={g}
                    size="small"
                    variant="outlined"
                    label={gradeLabel(g)}
                    sx={{ borderColor: "#c7d2fe", color: NAVY, fontWeight: 600 }}
                  />
                ))}
              </Stack>
            )}
          </Section>

          {/* Section D — Subjects */}
          <Section icon={<MenuBookOutlinedIcon fontSize="small" />} title={`Subjects (${row.class_subjects.length})`}>
            {row.class_subjects.length === 0 ? (
              <Typography sx={{ fontSize: 13, color: MUTED, fontStyle: "italic" }}>None</Typography>
            ) : (
              <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                {row.class_subjects.map((s, i) => (
                  <Chip
                    key={`${s}-${i}`}
                    size="small"
                    label={s}
                    sx={{ bgcolor: "#eff6ff", color: NAVY, fontWeight: 600 }}
                  />
                ))}
              </Stack>
            )}
          </Section>

          {/* Section E — Staff */}
          <Section icon={<BadgeOutlinedIcon fontSize="small" />} title="Staff">
            <Stack spacing={1}>
              <Field label="Name">
                {row.staff_name ? (
                  onViewStaff && row.staff_id ? (
                    <Link component="button" underline="hover"
                      onClick={() => { onViewStaff(row.staff_id!); onClose(); }}
                      sx={{ color: NAVY, fontWeight: 700, textAlign: "left" }}>
                      {row.staff_name}
                    </Link>
                  ) : row.staff_name
                ) : (
                  <Typography sx={{ fontSize: 13, color: MUTED, fontStyle: "italic" }}>Not assigned</Typography>
                )}
              </Field>
              <Field label="Location">
                {row.location || <Typography component="span" sx={{ color: MUTED, fontStyle: "italic", fontSize: 13 }}>Not assigned</Typography>}
              </Field>
            </Stack>
          </Section>

          {/* Section F — Schedule */}
          <Section icon={<EventOutlinedIcon fontSize="small" />} title="Schedule">
            <Stack spacing={1}>
              <Field label="Day">{row.day || "—"}</Field>
              <Field label="Time">
                {row.start_time ? `${to12h(row.start_time)} – ${to12h(row.end_time)}` : "—"}
              </Field>
              <Field label="Frequency">Weekly</Field>
            </Stack>
          </Section>

          {/* Section G — Mode & Location */}
          <Section icon={<PlaceOutlinedIcon fontSize="small" />} title="Mode & Location">
            <Stack spacing={1}>
              <Field label="Study mode">{modeLabel(row.study_mode)}</Field>
              <Field label="Location">
                {row.location || <Typography component="span" sx={{ color: MUTED, fontStyle: "italic", fontSize: 13 }}>Not assigned</Typography>}
              </Field>
            </Stack>
          </Section>

          {/* Section H — Assignment rules */}
          <Section icon={<AssignmentOutlinedIcon fontSize="small" />} title="Assignment">
            <Stack spacing={1}>
              <Field label="Assignment status">{status.label}</Field>
              <Field label="Date restriction">
                {!row.start_date && !row.end_date
                  ? "No date restriction"
                  : `${formatDate(row.start_date) || "—"} → ${row.end_date ? formatDate(row.end_date) : "Ongoing"}`}
              </Field>
            </Stack>
          </Section>
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* Footer actions */}
        <Stack direction="row" spacing={1} justifyContent="flex-end">
          {onViewClass && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<GroupsOutlinedIcon />}
              onClick={openFullClass}
              sx={{ textTransform: "none", borderRadius: 2, fontWeight: 600 }}
            >
              Open Full Class
            </Button>
          )}
          <Button
            size="small"
            variant="contained"
            onClick={onClose}
            sx={{ textTransform: "none", borderRadius: 2, fontWeight: 600, bgcolor: NAVY, "&:hover": { bgcolor: "#152d55" } }}
          >
            Close
          </Button>
        </Stack>
      </DialogContent>
    </Dialog>
  );
}
